<template>
    <div class="company-person-page">
        <breadcrumbs :text="personLabel" :company="company" />
        <page-title v-if="$conf.public.showPageTitles" :text="$t('page_titles.company_person_page_title')" />
        <div v-else class="text-h5 font-weight-bold py-2">
            {{ title }}
        </div>
        <v-tabs v-model="selectedTabIndex" class="mb-4">
            <v-tab v-for="tab in tabs" :key="tab.name" :data-cy="`tab-${tab.name}`">
                {{ tab.label }}
            </v-tab>
        </v-tabs>
        <v-window v-model="selectedTabIndex" class="company-person-page__window overflow--visible">
            <v-window-item v-for="tab in tabs" :key="tab.name">
                <person-form
                    v-if="tab.name === 'info'"
                    :person-data="person"
                    :departments="departments"
                    allow-no-credentials
                    :redirect="!person.personId"
                />
                <documents-form v-if="tab.name === 'passport'" :person="person" type="person" />
                <template v-if="tab.name === 'notificationSettings'">
                    <NotificationSettingsForm :person-id="person?.personId" />
                </template>
                <template v-if="tab.name === 'documents'">
                    <DocumentsTable
                        ref="dataTable"
                        :company-id="company.id"
                        :attach-to-id="person.personId"
                        :document-types="useUserDocument"
                    />
                </template>
                <template v-if="tab.name === 'communications'">
                    <communications
                        :communications="communications"
                        :communications-loading="communicationsLoading"
                        :communications-total-count="communicationsCount"
                        @get-communications="getCommunications"
                    >
                        <template v-slot="{getCommunications}">
                            <communications-filters
                                v-model="communicationFilters"
                                :has-active-filters="hasActiveCommunicationFilters"
                                @submit="getCommunications"
                                @reset="resetCommunicationFilters(getCommunications)"
                            />
                        </template>
                    </communications>
                </template>
            </v-window-item>
        </v-window>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import CompanyProfile from '~src/components/account/companyProfile.src'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import PersonForm from '~src/components/account/forms/personForm.src'
    import {useRecentViewsData} from '~/composables/recent-views'
    import {CHANGE_LOCALE_EVENT, EventBus} from '~/utils/event-bus'
    import type {PersonType} from '~/utils/types/account/companies/types'
    import DocumentsTable from '~/components/account/DocumentsTable.vue'
    import {initCommunicationFilters} from '~/utils/types/account/communications/helpers'
    import {clean, clone, isEqual} from '~/utils/helpers'
    import CommunicationsFilters from '~/components/account/communications/CommunicationsFilters.vue'
    import Communications from '~/components/account/communications/Communications.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'
    import DocumentsForm from '~/components/account/clients/DocumentsForm.vue'
    import NotificationSettingsForm from '~/components/account/clients/NotificationSettingsForm.vue'

    @Component({
        components: {
            NotificationSettingsForm,
            DocumentsForm,
            Communications,
            CommunicationsFilters,
            DocumentsTable,
            PersonForm,
            CompanyProfile,
            Breadcrumbs,
            PageTitle,
        },
    })
    export class CompanyPersonPage extends Vue {
        person: PersonType = {}
        company = {}
        departments = []
        selectedTabIndex: number = 0

        communications = []
        communicationsLoading: boolean = false
        communicationsCount: number = 0
        communicationFilters = initCommunicationFilters()

        async mounted() {
            if (authStore.isTO1 && this.$conf.public.reservationFilters.departments)
                EventBus.$on(CHANGE_LOCALE_EVENT, this.loadDepartments)

            const {params} = this.$route
            if (!params.id || params.id === '0') {
                this.person = {}
            } else await this.loadPerson()
            await this.loadDepartments()
            if (this.person.companyId) await this.loadCompany()
        }

        async loadPerson() {
            const {params} = this.$route
            try {
                const person = (
                    await this.$api.persons.get({id: params.id, ...(params.companyId && {companyId: params.companyId})})
                ).users[0]
                this.person = person
                this.person.active = JSON.parse(person.active)
                if (!person.firstName && person.firstNameLocalized) {
                    person.firstName = person.firstNameLocalized
                }
                if (!person.lastName && person.lastNameLocalized) {
                    person.lastName = person.lastNameLocalized
                }
                if (person) {
                    const linkName = `${person.firstName || ''} ${person.lastName || ''}`
                    await useRecentViewsData(this.$route, linkName.trim())
                }
            } catch (e) {
                this.person = {}
            }
        }

        async loadCompany() {
            try {
                const company = (await this.$api.companies.get({id: this.person.companyId})).companies[0]
                this.company = company
            } catch (e) {
                this.company = {}
            }
        }

        beforeUnmount() {
            if (authStore.isTO1 && this.$conf.public.reservationFilters.departments)
                EventBus.$off(CHANGE_LOCALE_EVENT, this.loadDepartments)
        }

        get tabs() {
            let tabs = [{name: 'info', label: this.$t('information')}]
            if (this.company?.type === 'CORPORATOR') {
                tabs = [...tabs, {name: 'passport', label: this.$t('passport')}]
            }
            if (this.$route.params.id !== '0') {
                const isSupplier =
                    this.company?.type === 'INTERNAL_SUPPLIER' || this.company?.type === 'HUB_SUPPLIER'
                tabs = [
                    ...tabs,
                    ...(isSupplier ? [] : [{name: 'documents', label: this.$t('documents')}]),
                    {name: 'notificationSettings', label: this.$t('notification_settings')},
                    // {name: 'communications', label: this.$t('communications')},
                ]
            }
            return tabs
        }

        async loadDepartments(): Promise<void> {
            try {
                const {departments} = await this.$api.departments.get()
                this.departments = departments
            } catch (e) {
                this.departments = []
            }
        }

        get personLabel() {
            if (this.person?.personId) {
                const {prefix, firstName, lastName} = this.person
                return `${prefix || ''} ${firstName || ''} ${lastName || ''}`
            }
            return this.$t('new_user_profile')
        }

        get title() {
            if (!this.person.firstName && !this.person.lastName) return this.$t('new_user_profile')
            return `${this.person.prefix || ''} ${this.person.firstName || ''} ${this.person.lastName || ''}`.trim()
        }

        async getCommunications({itemsPerPage, page}): Promise<void> {
            try {
                this.communicationsLoading = true
                const filters = clone(this.communicationFilters)
                clean(filters)
                if (filters.communicationDateFrom) filters.communicationDateFrom += 'T00:00'
                if (filters.createdDateFrom) filters.createdDateFrom += 'T00:00'
                if (filters.modifiedDateFrom) filters.modifiedDateFrom += 'T00:00'
                if (filters.communicationDateTo) filters.communicationDateTo += 'T23:59'
                if (filters.createdDateTo) filters.createdDateTo += 'T23:59'
                if (filters.modifiedDateTo) filters.modifiedDateTo += 'T23:59'
                const {communications, count} = await this.$api.communications.get({
                    ...filters,
                    ...{accountId: this.person.personId},
                    limit: itemsPerPage,
                    offset: itemsPerPage * (page - 1),
                    typeNEQ: 'NOTES',
                })
                this.communications = communications
                this.communicationsCount = count
                this.communicationsLoading = false
            } catch (e) {
                this.communicationsCount = 0
                this.communications = []
            } finally {
                this.communicationsLoading = false
            }
        }

        resetCommunicationFilters(getCommunications): void {
            this.communicationFilters = initCommunicationFilters()
            getCommunications()
        }

        get hasActiveCommunicationFilters(): boolean {
            return !isEqual(initCommunicationFilters(), this.communicationFilters)
        }

        get isTO1() {
            return authStore.isTO1
        }

        get selectedPersonRole() {
            if (!this.person?.role) return null
            const indexOfFirstDot = this.person.role.indexOf('.')

            return indexOfFirstDot ? this.person.role.substring(indexOfFirstDot + 1) : null
        }
    }

    export default CompanyPersonPage
</script>
